import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Grid,
  GridItem,
  Input,
  Select,
  Textarea,
  Button,
  Text,
  Progress,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useToast,
} from '@chakra-ui/react';

import {
  previewBatchFromCsv,
  submitBatchFromCsv,
  getBatchStatus,
  retryJob,
  regenerateWithVoice,
  deleteJob,
} from 'api/administrador/studioBatch';

const AUDIENCE_TARGETS = [
  { value: 'primaria', label: 'Primaria (1° a 5°)' },
  { value: 'secundaria', label: 'Secundaria (6° a 9°)' },
  { value: 'media', label: 'Media (10° a 11°)' },
  { value: 'tecnica', label: 'Carrera técnica' },
  { value: 'curso_general', label: 'Curso general' },
  { value: 'curso_ejecutivo', label: 'Curso ejecutivo' },
];

const STATE_LABEL = {
  queued: 'En cola',
  generating_script: 'Guion',
  generating_audio: 'Audio',
  rendering: 'Render',
  uploading: 'Subiendo',
  completed: 'Completado',
  failed: 'Falló',
};

const STATE_COLOR = {
  queued: 'gray',
  generating_script: 'purple',
  generating_audio: 'purple',
  rendering: 'purple',
  uploading: 'purple',
  completed: 'green',
  failed: 'red',
};

function defaultBatchId() {
  return `rescue-${new Date().toISOString().slice(0, 10)}`;
}

export default function RescateVideos() {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [csvText, setCsvText] = React.useState('');
  const [fileName, setFileName] = React.useState('');
  const [batchId, setBatchId] = React.useState(searchParams.get('batch') || defaultBatchId());
  const [lookupBatchId, setLookupBatchId] = React.useState(searchParams.get('batch') || '');
  const [excludeIdsText, setExcludeIdsText] = React.useState('');
  const [limit, setLimit] = React.useState('');
  // Voz seleccionada para todo el batch. '' = usar la voz por defecto de cada
  // audiencia. Presets: 'gonzalo' (Gonzalo es-CO), 'salome' (Dalia es-MX —
  // el ID legacy "salome" se preservó al migrar la voz base de Salomé a Dalia
  // para no invalidar batches en curso). El label en la UI se muestra como "Dalia".
  const [voicePreset, setVoicePreset] = React.useState('');
  const [preview, setPreview] = React.useState(null);
  const [analyzing, setAnalyzing] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [lookingUp, setLookingUp] = React.useState(false);
  const [batchStatus, setBatchStatus] = React.useState(null);
  const [regenAudience, setRegenAudience] = React.useState({});
  const itemsByMaterialId = React.useRef(new Map());
  const pollRef = React.useRef(null);

  React.useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Recupera automáticamente el batch de la URL al cargar/recargar la
  // página — sin esto, refrescar el navegador perdía la vista del progreso
  // (el job seguía vivo en Redis, solo la pantalla lo olvidaba).
  React.useEffect(() => {
    const fromUrl = searchParams.get('batch');
    if (fromUrl) {
      handleLookupBatch(fromUrl);
    }
  }, []);

  const handleLookupBatch = async (idOverride) => {
    const id = idOverride || lookupBatchId;
    if (!id) {
      toast({ title: 'Escribe un ID de batch primero', status: 'warning', duration: 3000 });
      return;
    }
    setLookingUp(true);
    try {
      const status = await getBatchStatus(id);
      setBatchStatus(status);
      setBatchId(id);
      setSearchParams({ batch: id });
      pollBatch(id);
    } catch (error) {
      toast({
        title: 'No se encontró ese batch',
        description: 'Puede que ya haya expirado (TTL de 30 días) o el ID esté mal escrito.',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setLookingUp(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setPreview(null);
    setBatchStatus(null);
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ''));
    reader.readAsText(file);
  };

  const parseExcludeIds = () =>
    excludeIdsText
      .split(/[\s,]+/)
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isInteger(n) && n > 0);

  const handleAnalyze = async () => {
    if (!csvText) {
      toast({ title: 'Selecciona un archivo CSV primero', status: 'warning', duration: 3000 });
      return;
    }
    setAnalyzing(true);
    try {
      const result = await previewBatchFromCsv(csvText, {
        batchId,
        excludeMaterialIds: parseExcludeIds(),
        limit: limit ? Number(limit) : undefined,
        voicePreset: voicePreset || undefined,
      });
      setPreview(result);
      itemsByMaterialId.current = new Map(result.items.map((item) => [item.material_id, item]));
      toast({ title: 'CSV analizado', status: 'success', duration: 2000 });
    } catch (error) {
      toast({
        title: 'No se pudo analizar el CSV',
        description: error?.response?.data?.message || error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const pollBatch = (id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const status = await getBatchStatus(id);
        setBatchStatus(status);
        const pending = status.summary.queued + status.summary.generating_script +
          status.summary.generating_audio + status.summary.rendering + status.summary.uploading;
        if (pending === 0) clearInterval(pollRef.current);
      } catch (error) {
        clearInterval(pollRef.current);
      }
    }, 5000);
  };

  const handleConfirmAndEnqueue = async () => {
    setSubmitting(true);
    try {
      const result = await submitBatchFromCsv(csvText, {
        batchId,
        excludeMaterialIds: parseExcludeIds(),
        limit: limit ? Number(limit) : undefined,
        voicePreset: voicePreset || undefined,
      });
      toast({
        title: `Batch encolado — ${result.jobs.length} videos`,
        status: 'success',
        duration: 3000,
      });
      setSearchParams({ batch: batchId });
      const status = await getBatchStatus(batchId);
      setBatchStatus(status);
      pollBatch(batchId);
    } catch (error) {
      toast({
        title: 'No se pudo encolar el batch',
        description: error?.response?.data?.error || error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = async (jobId) => {
    try {
      await retryJob(jobId);
      toast({ title: 'Job reencolado', status: 'success', duration: 2000 });
      const status = await getBatchStatus(batchId);
      setBatchStatus(status);
      pollBatch(batchId);
    } catch (error) {
      toast({ title: 'No se pudo reencolar el job', status: 'error', duration: 3000 });
    }
  };

  const handleDelete = async (jobId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`¿Eliminar el job ${jobId} del batch? Esta acción no se puede deshacer.`)) {
      return;
    }
    try {
      await deleteJob(jobId, batchId);
      toast({ title: 'Job eliminado', status: 'success', duration: 2000 });
      const status = await getBatchStatus(batchId);
      setBatchStatus(status);
    } catch (error) {
      toast({
        title: 'No se pudo eliminar el job',
        description: error?.response?.data?.error || error.message,
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleRegenerate = async (materialId) => {
    const item = itemsByMaterialId.current.get(materialId);
    const audience = regenAudience[materialId];
    if (!item || !audience) {
      toast({ title: 'Selecciona una audiencia primero', status: 'warning', duration: 3000 });
      return;
    }
    try {
      await regenerateWithVoice(item, audience);
      toast({ title: `Regenerando material ${materialId} con voz "${audience}"`, status: 'success', duration: 2500 });
      const status = await getBatchStatus(batchId);
      setBatchStatus(status);
      pollBatch(batchId);
    } catch (error) {
      toast({ title: 'No se pudo regenerar', status: 'error', duration: 3000 });
    }
  };

  return (
    <Box p={4}>
      <Text fontSize="xl" fontWeight="700" mb={6}>
        Klazia Studio — Rescate de Videos (Batch)
      </Text>

      <Box borderWidth="1px" borderRadius="8px" p={4} mb={6} bg="gray.50">
        <Text fontWeight="600" mb={2}>¿Ya encolaste un batch antes? (ej: recargaste la página)</Text>
        <Box display="flex" gap={3} alignItems="flex-start">
          <Input
            value={lookupBatchId}
            onChange={(e) => setLookupBatchId(e.target.value)}
            placeholder="ID del batch, ej: rescue-2026-07-03"
            maxWidth="320px"
          />
          <Button onClick={() => handleLookupBatch()} isLoading={lookingUp}>
            Ver estado
          </Button>
        </Box>
        <Text fontSize="xs" color="gray.500" mt={2}>
          El progreso queda guardado en el servidor por 30 días — recargar la página no lo pierde,
          solo hay que volver a consultarlo con su ID (o usar el link con <code>?batch=...</code> en la URL).
        </Text>
      </Box>

      <Grid templateColumns="repeat(2, 1fr)" gap={8}>
        <GridItem colSpan={1}>
          <Box fontWeight="500">Archivo CSV (impacto_prioridad_1A.csv o similar)</Box>
          <Input type="file" accept=".csv" onChange={handleFileChange} marginTop="6px" marginBottom="12px" />
          {fileName && <Text fontSize="sm" color="gray.500" mb={3}>{fileName}</Text>}

          <Box fontWeight="500">ID del batch</Box>
          <Input value={batchId} onChange={(e) => setBatchId(e.target.value)} marginTop="6px" marginBottom="12px" />

          <Box fontWeight="500">Excluir material_id (separados por coma o salto de línea)</Box>
          <Textarea
            value={excludeIdsText}
            onChange={(e) => setExcludeIdsText(e.target.value)}
            placeholder="Ej: los material_id ya cubiertos por grabación humana (hipercompartidos)"
            marginTop="6px"
            marginBottom="12px"
          />

          <Box fontWeight="500">Límite (opcional, ej: 50 para el piloto)</Box>
          <Input
            type="number"
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            placeholder="Dejar vacío para procesar todo"
            marginTop="6px"
            marginBottom="12px"
          />

          <Box fontWeight="500">Voz del batch</Box>
          <Select
            value={voicePreset}
            onChange={(e) => setVoicePreset(e.target.value)}
            marginTop="6px"
            marginBottom="12px"
          >
            <option value="">Por defecto (según audiencia de cada video)</option>
            <option value="gonzalo">Gonzalo (masculina — natural, narrador)</option>
            <option value="salome">Dalia (femenina — natural, narradora)</option>
          </Select>

          <Button
            colorScheme="messenger"
            onClick={handleAnalyze}
            isLoading={analyzing}
            isDisabled={analyzing || !csvText}
            mr={3}
          >
            1. Analizar CSV
          </Button>

          {preview && (
            <Button colorScheme="green" onClick={handleConfirmAndEnqueue} isLoading={submitting}>
              2. Confirmar y encolar ({preview.items_to_enqueue})
            </Button>
          )}

          {preview && (
            <Box mt={6} borderWidth="1px" borderRadius="8px" p={4}>
              <Text fontWeight="600" mb={2}>Resultado del análisis</Text>
              <Text fontSize="sm">Filas leídas: {preview.rows_read}</Text>
              <Text fontSize="sm">Items válidos: {preview.valid_items}</Text>
              <Text fontSize="sm">A encolar: {preview.items_to_enqueue}</Text>
              <Text fontSize="sm">Excluidos: {preview.excluded_count}</Text>
              <Text fontSize="sm" color={preview.needs_review_count > 0 ? 'orange.500' : 'gray.500'}>
                Necesitan revisión: {preview.needs_review_count}
              </Text>
              <Text fontSize="sm" color="gray.500">
                Audiencia derivada automáticamente: {preview.derived_audience_count}
              </Text>
            </Box>
          )}
        </GridItem>

        <GridItem colSpan={1}>
          {batchStatus && (
            <Box borderWidth="1px" borderRadius="8px" p={4}>
              <Text fontWeight="600" mb={3}>Batch {batchStatus.batch_id} — {batchStatus.total} videos</Text>
              <Box mb={3}>
                {Object.entries(batchStatus.summary).map(([state, count]) => (
                  <Badge key={state} colorScheme={STATE_COLOR[state]} mr={2} mb={2}>
                    {STATE_LABEL[state] || state}: {count}
                  </Badge>
                ))}
              </Box>
              <Progress
                value={(batchStatus.summary.completed / batchStatus.total) * 100}
                colorScheme="green"
                borderRadius="4px"
                mb={4}
              />

              <TableContainer maxHeight="500px" overflowY="auto">
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Material</Th>
                      <Th>Estado</Th>
                      <Th>Progreso</Th>
                      <Th>Acciones</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {batchStatus.jobs.map((job) => (
                      <Tr key={job.job_id}>
                        <Td>{job.material_id}</Td>
                        <Td>
                          <Badge colorScheme={STATE_COLOR[job.state]}>{STATE_LABEL[job.state] || job.state}</Badge>
                        </Td>
                        <Td>{job.progress}%</Td>
                        <Td>
                          {job.state === 'failed' && (
                            <Button size="xs" mr={2} onClick={() => handleRetry(job.job_id)}>
                              Reintentar
                            </Button>
                          )}
                          <Select
                            size="xs"
                            display="inline-block"
                            width="140px"
                            placeholder="Otra voz"
                            mr={2}
                            value={regenAudience[job.material_id] || ''}
                            onChange={(e) =>
                              setRegenAudience((prev) => ({ ...prev, [job.material_id]: e.target.value }))
                            }
                          >
                            {AUDIENCE_TARGETS.map((opt) => (
                              <option value={opt.value} key={opt.value}>{opt.label}</option>
                            ))}
                          </Select>
                          <Button size="xs" onClick={() => handleRegenerate(job.material_id)}>
                            Regenerar
                          </Button>
                          <Button
                            size="xs"
                            ml={2}
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => handleDelete(job.job_id)}
                          >
                            Eliminar
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </GridItem>
      </Grid>
    </Box>
  );
}
